import {makeSelectable, SelectableColumn} from "../../components/ColumnSelection";
// import {IState} from "../../reducer/Reducer";
import {
    isInitiallyVisible,
    createColumn,
    ColumnParams,
    OptionColumnKeys,
    DerivateStatesKey,
} from "./OptionColumns";

/**
 * Settings version.
 *
 * Increase the version for every incompatible change to the rest of the
 * settings interface. Compatibility is measured by JSON.stringify/parse.
 *
 * Incompatible settings loaded from the back end are discarded and the
 * user is reverted back the the default settings.
 */
const VERSION = 7;

export interface IOptionsViewUserSettings {
    /** The version of the options settings instance. */
    version: number;

    /** Columns shown in the OptionsView tables. */
    optionColumns: {key: string, visible: boolean}[] | undefined;

    /** The derivates to hide when displaying the derivates column. */
    hiddenDerivates: string[];

    /** Shows filters in header if true. */
    showFilter: boolean;

    /** Defines whether the list of shown derivates can be edited. */
    editDerivates: boolean;

    /** Defines whether to show options, which have for all shown derivates value as n.A. */
    showNotAvailableOptions: boolean;

    /** Defines whether reordering of columns is enabled. */
    enableReorderColumns: boolean;
}

/** initial UserSettings saved in global state */
export const initSettings = (): IOptionsViewUserSettings => {
    return {
        version: VERSION,
        optionColumns: undefined,
        hiddenDerivates: [],
        showFilter: true,
        editDerivates: false,
        showNotAvailableOptions: true,
        enableReorderColumns: false,
    };
};

/**
 * Validate a given instance of options view settings.
 *
 * This method is called after the general user settings were deserialized
 * from persistent storage (database) and passed the version check.
 *
 * Here we can check the specific option view settings version and
 * validate whether it's still compatible. It can also be used to migrate
 * an older option view settings version to the current layout.
 *
 * This allows the option view settings to change and become incompatible
 * without affecting the general user settings.
 */
export const validateSettings = (settings?: IOptionsViewUserSettings): IOptionsViewUserSettings => {
    if (settings && settings.version !== undefined && settings.version === VERSION) {
        // latest version; nothing to do
        return settings;
    }
    // incompatible settings, return the default base options settings.
    const defaultSettings = initSettings();
    if (settings) {
        // settings exist but their content is incompatible;
        // this is the place to migrate settings to the latest version
        settings.version = defaultSettings.version;
        settings.optionColumns = defaultSettings.optionColumns;
        settings.hiddenDerivates = defaultSettings.hiddenDerivates;
        settings.showFilter = defaultSettings.showFilter;
        settings.editDerivates = defaultSettings.editDerivates;
        settings.showNotAvailableOptions = defaultSettings.showNotAvailableOptions;
        settings.enableReorderColumns = defaultSettings.enableReorderColumns;
        return settings;
    } else {
        // no settings object for options view, yet
        return defaultSettings;
    }
};

/** Create columns for OptionsView from the settings. */
export const loadColumns = (settings: IOptionsViewUserSettings, state: any, params?: ColumnParams): SelectableColumn[] => {
    const processParams = (key: string, p?: ColumnParams): ColumnParams | undefined => {
        if (key !== DerivateStatesKey) {
            // nothing to do
            return p;
        }
        // add the derivates to hide to the derivates column
        const hiddenDerivates = [...(settings.hiddenDerivates || [])];
        if (!p) {
            // no provided column params. create our own params object.
            return {[key]: {hideDerivates: hiddenDerivates}};
        }
        if (!p.hasOwnProperty(key)) {
            // no provided params for this specific column. create our own params object.
            return Object.assign(p, {[key]: {hideDerivates: hiddenDerivates}});
        }
        // add the status parameter to the column's params
        Object.assign(p[key], {hideDerivates: hiddenDerivates});
        return p;
    };
    const newColumn = (key: string, p?: ColumnParams): SelectableColumn => (
        makeSelectable(createColumn(state, key, processParams(key, p)), isInitiallyVisible(key))
    );
    const loadColumn = (setting: {key: string, visible: boolean}, p?: ColumnParams): SelectableColumn => (
        makeSelectable(createColumn(state, setting.key, processParams(setting.key, p)), setting.visible)
    );

    const {optionColumns} = settings;
    const columns = optionColumns && optionColumns.length > 0
        ? optionColumns.map(columnSetting => loadColumn(columnSetting, params))
        : OptionColumnKeys.map(columnKey => newColumn(columnKey, params));
    return columns;
};

/** Save OptionView columns in the settings and return the new settings object. */
export const saveColumns = (settings: IOptionsViewUserSettings, columns: SelectableColumn[]): IOptionsViewUserSettings => {
    const newSettings = Object.assign({}, settings);
    newSettings.optionColumns = columns.map(column => (
        {key: column.key, visible: column.visible}
    ));
    return newSettings;
};

/** Save OptionView derivate visibility in the settings and return the new settings object. */
export const saveDerivateVisibility = (settings: IOptionsViewUserSettings, hiddenDerivates: string[]): IOptionsViewUserSettings => {
    const newSettings = Object.assign({}, settings);
    newSettings.hiddenDerivates = [...hiddenDerivates];
    return newSettings;
};



// WEBPACK FOOTER //
// ./src/views/OptionsView/OptionsViewSettings.ts